import { useState, useEffect, useCallback } from 'react'
import Markdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

const API_BASE = `${import.meta.env.VITE_API_URL || 'http://localhost:5001'}/api/portfolio-manager`

interface SavedReport {
  id: string
  title: string
  category: string
  source: string
  createdAt: string
}

export default function ReportReaderPage() {
  const [markdown, setMarkdown] = useState<string>('')
  const [fileName, setFileName] = useState<string>('')
  const [isDragOver, setIsDragOver] = useState(false)
  const [savedReports, setSavedReports] = useState<SavedReport[]>([])
  const [selectedReportId, setSelectedReportId] = useState<string | null>(null)
  const [showSaved, setShowSaved] = useState(true)
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set())
  const [multiSelectMode, setMultiSelectMode] = useState(false)
  const [renamingId, setRenamingId] = useState<string | null>(null)
  const [renameValue, setRenameValue] = useState('')
  const [applying, setApplying] = useState(false)

  // Fetch saved reports (on mount and on window focus)
  const fetchReports = useCallback(() => {
    fetch(`${API_BASE}/reports`)
      .then(r => r.ok ? r.json() : { reports: [] })
      .then(data => setSavedReports(data.reports || []))
      .catch(() => {})
  }, [])

  useEffect(() => {
    fetchReports()
    window.addEventListener('focus', fetchReports)
    return () => window.removeEventListener('focus', fetchReports)
  }, [fetchReports])

  const handleFile = useCallback((file: File) => {
    if (!file.name.endsWith('.md') && !file.name.endsWith('.markdown') && !file.name.endsWith('.txt')) {
      alert('Please upload a .md or .txt file')
      return
    }
    setFileName(file.name)
    setSelectedReportId(null)
    const reader = new FileReader()
    reader.onload = (e) => {
      setMarkdown(e.target?.result as string || '')
    }
    reader.readAsText(file)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    const file = e.dataTransfer.files[0]
    if (file) handleFile(file)
  }, [handleFile])

  const handleFileInput = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) handleFile(file)
  }, [handleFile])

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData('text/plain')
    if (text) {
      setMarkdown(text)
      setFileName('Pasted content')
      setSelectedReportId(null)
    }
  }, [])

  const handleClear = () => {
    setMarkdown('')
    setFileName('')
    setSelectedReportId(null)
  }

  const loadReport = async (report: SavedReport) => {
    try {
      const resp = await fetch(`${API_BASE}/reports/${report.id}`)
      if (!resp.ok) throw new Error('Failed to load')
      const data = await resp.json()
      setMarkdown(data.content)
      setFileName(data.title)
      setSelectedReportId(report.id)
    } catch {
      alert('Failed to load report')
    }
  }

  const fixReport = async () => {
    if (!markdown || !selectedReportId) return
    let fixed = markdown

    // Fix 1: Strip ```markdown ... ``` wrappers (DeepSeek wraps output in code blocks — may have multiple)
    if (fixed.includes('```markdown') || fixed.includes('```md')) {
      const blocks: string[] = []
      const blockRegex = /```(?:markdown|md)\s*\n([\s\S]*?)(?:\n```|$)/g
      let match: RegExpExecArray | null
      let hasBlocks = false
      while ((match = blockRegex.exec(fixed)) !== null) {
        blocks.push(match[1].trim())
        hasBlocks = true
      }
      if (hasBlocks && blocks.length > 0) {
        fixed = blocks.join('\n\n---\n\n')
      }
    }

    // Fix 2: Extract from preamble + ```json code block (Claude preamble issue)
    if (fixed.includes('```json') || fixed.includes('```\n{')) {
      const codeStart = fixed.indexOf('```json') >= 0 ? fixed.indexOf('```json') : fixed.indexOf('```\n{')
      if (codeStart >= 0) {
        const after = fixed.slice(codeStart)
        const lines = after.split('\n')
        const stripped = lines.slice(1)
        if (stripped.length && stripped[stripped.length - 1].trim() === '```') stripped.pop()
        const inner = stripped.join('\n').trim()
        try {
          const parsed = JSON.parse(inner)
          if (parsed && typeof parsed.response === 'string') {
            fixed = parsed.response
          }
        } catch {
          // Try regex extraction for truncated JSON
          const match = inner.match(/"response"\s*:\s*"/)
          if (match) {
            const start = inner.indexOf(match[0]) + match[0].length
            let rest = inner.slice(start)
            const endMarkers = ['", "suggestions"', '","suggestions"', '",\n"suggestions"']
            let bestEnd = rest.length
            for (const marker of endMarkers) {
              const pos = rest.indexOf(marker)
              if (pos > 0 && pos < bestEnd) bestEnd = pos
            }
            fixed = rest.slice(0, bestEnd).replace(/\\n/g, '\n').replace(/\\t/g, '\t').replace(/\\"/g, '"').replace(/\\\\/g, '\\')
          }
        }
      }
    }
    // Fix 3: Raw JSON object {"response": "..."}  (may be prettified with newlines)
    else if (fixed.trim().startsWith('{') && fixed.includes('"response"')) {
      try {
        const parsed = JSON.parse(fixed.trim())
        if (parsed && typeof parsed.response === 'string') {
          fixed = parsed.response
        }
      } catch {
        const match = fixed.match(/"response"\s*:\s*"/)
        if (match) {
          const start = fixed.indexOf(match[0]) + match[0].length
          let rest = fixed.slice(start)
          const endMarkers = ['", "suggestions"', '","suggestions"', '",\n"suggestions"']
          let bestEnd = rest.length
          for (const marker of endMarkers) {
            const pos = rest.indexOf(marker)
            if (pos > 0 && pos < bestEnd) bestEnd = pos
          }
          fixed = rest.slice(0, bestEnd).replace(/\\n/g, '\n').replace(/\\t/g, '\t').replace(/\\"/g, '"').replace(/\\\\/g, '\\')
        }
      }
    }
    // Fix 4: Content has literal \n escape sequences (not real newlines)
    else if (!fixed.includes('\n') && fixed.includes('\\n')) {
      fixed = fixed.replace(/\\n/g, '\n').replace(/\\t/g, '\t').replace(/\\"/g, '"').replace(/\\\\/g, '\\')
    }

    if (fixed === markdown) {
      alert('Report appears to already be in correct format.')
      return
    }

    // Save the fixed content back to Supabase
    try {
      const resp = await fetch(`${API_BASE}/reports/${selectedReportId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ content: fixed }),
      })
      if (!resp.ok) throw new Error('Save failed')
      setMarkdown(fixed)
      alert('Report fixed and saved!')
    } catch {
      // If PUT isn't supported, just update display
      setMarkdown(fixed)
      alert('Report reformatted (display only — save manually with 💾 Save button)')
    }
  }

  const deleteReport = async (id: string, title: string) => {
    if (!confirm(`Delete "${title}"?`)) return
    try {
      await fetch(`${API_BASE}/reports/${id}`, { method: 'DELETE' })
      setSavedReports(prev => prev.filter(r => r.id !== id))
      if (selectedReportId === id) handleClear()
      selectedIds.delete(id)
      setSelectedIds(new Set(selectedIds))
    } catch { /* ignore */ }
  }

  const toggleSelect = (id: string) => {
    const next = new Set(selectedIds)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelectedIds(next)
  }

  const selectAll = () => {
    setSelectedIds(new Set(savedReports.map(r => r.id)))
  }

  const bulkDelete = async () => {
    if (selectedIds.size === 0) return
    if (!confirm(`Delete ${selectedIds.size} selected report(s)?`)) return
    for (const id of selectedIds) {
      try { await fetch(`${API_BASE}/reports/${id}`, { method: 'DELETE' }) } catch { /* ignore */ }
    }
    setSavedReports(prev => prev.filter(r => !selectedIds.has(r.id)))
    if (selectedReportId && selectedIds.has(selectedReportId)) handleClear()
    setSelectedIds(new Set())
    setMultiSelectMode(false)
  }

  const exportSelected = async () => {
    if (selectedIds.size === 0) return
    for (const id of selectedIds) {
      try {
        const resp = await fetch(`${API_BASE}/reports/${id}`)
        if (!resp.ok) continue
        const data = await resp.json()
        const content = data.content || ''
        const title = (data.title || 'report').replace(/[^a-zA-Z0-9-_ ]/g, '').trim()
        const blob = new Blob([content], { type: 'text/markdown' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${title}.md`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
      } catch { /* skip */ }
    }
  }

  const renameReport = async (id: string) => {
    const newTitle = renameValue.trim()
    if (!newTitle) { setRenamingId(null); return }
    try {
      await fetch(`${API_BASE}/reports/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newTitle }),
      })
      setSavedReports(prev => prev.map(r => r.id === id ? { ...r, title: newTitle } : r))
      if (selectedReportId === id) setFileName(newTitle)
    } catch { /* ignore */ }
    setRenamingId(null)
  }

  const applyToConstitution = async () => {
    if (!selectedReportId || !markdown) return
    if (!confirm('Apply this report\'s learnings to the Constitution?\n\nThis will:\n1. Parse all Learning sections\n2. Insert content into the relevant Constitution sections\n3. Append the Version Update to the Change Log\n\nA version backup will be saved automatically.')) return

    setApplying(true)
    try {
      const resp = await fetch(`${API_BASE}/constitution/apply-report`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reportId: selectedReportId }),
      })
      const data = await resp.json()
      if (!resp.ok) {
        alert(`Failed to apply: ${data.error || 'Unknown error'}${data.warnings?.length ? '\n\nWarnings:\n' + data.warnings.join('\n') : ''}`)
        return
      }
      let msg = `✅ Constitution updated to ${data.newVersion}\n\n${data.learningsApplied} learning(s) applied to:\n${data.sectionsModified?.join('\n• ') || 'N/A'}`
      if (data.warnings?.length) {
        msg += `\n\n⚠️ Warnings:\n${data.warnings.join('\n')}`
      }
      alert(msg)
    } catch (e) {
      alert(`Error: ${e instanceof Error ? e.message : 'Network error'}`)
    } finally {
      setApplying(false)
    }
  }

  const saveCurrentAsReport = async () => {
    if (!markdown.trim()) return
    const title = prompt('Report title:', fileName || `Report ${new Date().toLocaleDateString()}`)
    if (!title) return

    try {
      const resp = await fetch(`${API_BASE}/reports`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ title, content: markdown, category: 'manual', source: 'upload' }),
      })
      if (!resp.ok) throw new Error('Failed to save')
      const data = await resp.json()
      setSavedReports(prev => [{ id: data.id, title: data.title, category: 'manual', source: 'upload', createdAt: data.createdAt }, ...prev])
    } catch {
      alert('Failed to save report')
    }
  }

  return (
    <div className="flex h-full">
      {/* Saved Reports Sidebar */}
      {showSaved && (
        <div className="w-64 shrink-0 border-r border-[var(--color-border)] bg-[var(--color-bg-secondary)] flex flex-col">
          <div className="p-4 border-b border-[var(--color-border)] flex items-center justify-between shrink-0">
            <h3 className="text-xs font-bold text-[var(--color-text-muted)] uppercase">Saved Reports</h3>
            <button
              onClick={() => { setMultiSelectMode(!multiSelectMode); if (multiSelectMode) setSelectedIds(new Set()) }}
              className={`text-[10px] px-2 py-0.5 rounded transition-colors ${multiSelectMode ? 'bg-blue-600 text-white' : 'text-[var(--color-text-muted)] hover:text-[var(--color-text-primary)]'}`}
            >
              {multiSelectMode ? '✓ Done' : '☐ Select'}
            </button>
          </div>
          {/* Multi-select action bar */}
          {multiSelectMode && selectedIds.size > 0 && (
            <div className="px-3 py-2 border-b border-[var(--color-border)] bg-[var(--color-bg-tertiary)] flex items-center gap-2 shrink-0">
              <span className="text-[10px] text-[var(--color-text-muted)]">{selectedIds.size} selected</span>
              <button onClick={selectAll} className="text-[10px] text-blue-400 hover:underline">All</button>
              <div className="flex-1" />
              <button onClick={exportSelected} className="text-[10px] px-2 py-0.5 rounded bg-green-700 text-white hover:bg-green-600">⬇ Export</button>
              <button onClick={bulkDelete} className="text-[10px] px-2 py-0.5 rounded bg-red-700 text-white hover:bg-red-600">🗑 Delete</button>
            </div>
          )}
          {multiSelectMode && selectedIds.size === 0 && (
            <div className="px-3 py-2 border-b border-[var(--color-border)] bg-[var(--color-bg-tertiary)] flex items-center gap-2 shrink-0">
              <span className="text-[10px] text-[var(--color-text-muted)]">Select reports below</span>
              <button onClick={selectAll} className="text-[10px] text-blue-400 hover:underline">Select all</button>
            </div>
          )}
          <div className="flex-1 overflow-y-auto">
          {savedReports.length === 0 ? (
            <div className="p-4 text-xs text-[var(--color-text-muted)] text-center">
              No saved reports yet. Reports from AI chat will appear here.
            </div>
          ) : (
            <div className="divide-y divide-[var(--color-border)]">
              {savedReports.map(report => (
                <div
                  key={report.id}
                  className={`p-3 cursor-pointer hover:bg-[var(--color-bg-tertiary)] transition-colors ${
                    selectedReportId === report.id ? 'bg-[var(--color-accent)]/10 border-l-2 border-l-[var(--color-accent)]' : ''
                  } ${selectedIds.has(report.id) ? 'bg-blue-900/20' : ''}`}
                >
                  <div className="flex items-start gap-2">
                    {multiSelectMode && (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(report.id)}
                        onChange={() => toggleSelect(report.id)}
                        className="mt-0.5 rounded border-gray-400"
                      />
                    )}
                    <div className="flex-1 min-w-0" onClick={() => multiSelectMode ? toggleSelect(report.id) : loadReport(report)} onDoubleClick={(e) => { if (!multiSelectMode) { e.stopPropagation(); setRenamingId(report.id); setRenameValue(report.title) } }}>
                      {renamingId === report.id ? (
                        <input
                          type="text"
                          value={renameValue}
                          onChange={(e) => setRenameValue(e.target.value)}
                          onKeyDown={(e) => { if (e.key === 'Enter') renameReport(report.id); if (e.key === 'Escape') setRenamingId(null) }}
                          onBlur={() => renameReport(report.id)}
                          autoFocus
                          className="w-full text-xs px-1 py-0.5 rounded border border-blue-400 bg-white text-gray-900 focus:outline-none"
                        />
                      ) : (
                        <p className="text-xs font-medium text-[var(--color-text-primary)] truncate">{report.title}</p>
                      )}
                      <p className="text-[10px] text-[var(--color-text-muted)] mt-0.5">
                        {new Date(report.createdAt).toLocaleDateString()}
                        {report.source === 'ai_chat' && ' · AI'}
                      </p>
                    </div>
                    {!multiSelectMode && (
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteReport(report.id, report.title) }}
                        className="text-[10px] text-[var(--color-text-muted)] hover:text-red-400 px-1"
                      >
                        🗑️
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          </div>
        </div>
      )}

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-3 border-b border-[var(--color-border)] bg-[var(--color-bg-secondary)] shrink-0">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSaved(!showSaved)}
              className={`px-2 py-1 text-xs rounded border transition-colors ${showSaved ? 'bg-[var(--color-accent)]/10 border-[var(--color-accent)] text-[var(--color-accent)]' : 'border-[var(--color-border)] text-[var(--color-text-muted)]'}`}
            >
              📚
            </button>
            <h1 className="text-lg font-bold text-[var(--color-text-primary)]">📄 Report Reader</h1>
            {fileName && <span className="text-xs text-[var(--color-text-muted)]">— {fileName}</span>}
          </div>
          <div className="flex items-center gap-2">
            {markdown && (
              <>
                {selectedReportId && (
                  <button onClick={applyToConstitution} disabled={applying} className="px-3 py-1.5 text-xs rounded border border-purple-500/50 text-purple-400 hover:bg-purple-500/10 disabled:opacity-50 disabled:cursor-not-allowed">
                    {applying ? '⏳ Applying...' : '📜 Apply to Constitution'}
                  </button>
                )}
                {selectedReportId && (
                  <button onClick={fixReport} className="px-3 py-1.5 text-xs rounded border border-amber-500/50 text-amber-400 hover:bg-amber-500/10">
                    🔧 Fix
                  </button>
                )}
                <button onClick={saveCurrentAsReport} className="px-3 py-1.5 text-xs rounded bg-blue-600 text-white hover:bg-blue-500">
                  💾 Save
                </button>
                <button onClick={handleClear} className="px-3 py-1.5 text-xs rounded border border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-red-400 hover:text-red-400">
                  Clear
                </button>
              </>
            )}
            <label className="px-3 py-1.5 text-xs rounded border border-[var(--color-border)] text-[var(--color-text-muted)] hover:border-[var(--color-accent)] hover:text-[var(--color-accent)] cursor-pointer">
              📂 Open File
              <input type="file" accept=".md,.markdown,.txt" onChange={handleFileInput} className="hidden" />
            </label>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto" onPaste={handlePaste}>
          {markdown ? (
            <div className="p-6 report-content">
              <Markdown remarkPlugins={[remarkGfm]}>{markdown}</Markdown>
            </div>
          ) : (
            <div
              onDrop={handleDrop}
              onDragOver={(e) => { e.preventDefault(); setIsDragOver(true) }}
              onDragLeave={() => setIsDragOver(false)}
              className={`flex flex-col items-center justify-center h-full p-12 text-center transition-colors ${
                isDragOver ? 'bg-[var(--color-accent)]/5' : ''
              }`}
            >
              <span className="text-5xl mb-4">📄</span>
              <h2 className="text-xl font-semibold text-[var(--color-text-primary)] mb-2">Report Reader</h2>
              <p className="text-sm text-[var(--color-text-muted)] mb-4">
                Drop a .md file here, click "Open File", paste markdown (Ctrl+V), or select a saved report from the sidebar.
              </p>
              <p className="text-xs text-[var(--color-text-muted)]">
                AI-generated reports from chat will automatically appear in the sidebar.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
